import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { IntensityByModalityResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { formatDecimal } from '../../utils/localeFormat';
import { modalityColors, chartChrome } from '../../utils/chartColors';
import './IntensityByModalityChart.scss';

interface IntensityByModalityChartProps {
  data: IntensityByModalityResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const IntensityByModalityChart: FC<IntensityByModalityChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const modalities = data.data.map((d) => d.modality);
    const values = data.data.map((d) => d.intensityKgPerTon);

    const modality = modalityColors();
    const chrome = chartChrome();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.name}<br/>${p.marker} ${formatDecimal(Number(p.value), 2, lng)} kg/ton`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: modalities,
        axisLabel: { fontSize: 11, color: chrome.muted, rotate: 30 },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.perTon')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { color: chrome.muted, formatter: (v: number) => formatDecimal(v, 1, lng) },
        splitLine: { lineStyle: { type: 'dotted', color: chrome.grid } },
      },
      series: [
        {
          type: 'bar',
          data: values.map((v, i) => ({
            value: v,
            itemStyle: { color: modality[i % modality.length] },
          })),
          barMaxWidth: 60,
          label: {
            show: true,
            position: 'top',
            fontSize: 11,
            formatter: (params: any) => formatDecimal(Number(params.value), 2, lng),
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, lng, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="intensity-by-modality-chart">
        <h3 className="intensity-by-modality-chart__title">{t('cpi.analytics.intensityByModality')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="intensity-by-modality-chart intensity-by-modality-chart--loading">
        <h3 className="intensity-by-modality-chart__title">{t('cpi.analytics.intensityByModality')}</h3>
        <div className="intensity-by-modality-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="intensity-by-modality-chart">
        <h3 className="intensity-by-modality-chart__title">{t('cpi.analytics.intensityByModality')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="intensity-by-modality-chart">
      <h3 className="intensity-by-modality-chart__title">{t('cpi.analytics.intensityByModality')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default IntensityByModalityChart;
